import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, MessageCircle, Settings2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CampoData from "@/components/common/CampoData";
import { useConferma } from "@/hooks/use-conferma";
import { erroreDi, giorniDellaSettimana, giorno, oggi, settimanaDi, testaGiorno } from "@/lib/crm";
import { giorniDelLavoro, linkAvviso, primoGiornoProposto } from "@/lib/montaggio";
import { cn } from "@/lib/utils";

/** «lun 16 nov – mer 18 nov · 3 giorni» */
function riepilogo(giorni) {
  const n = giorni.length;
  const arco = n === 1 ? giorno(giorni[0]) : `${giorno(giorni[0])} – ${giorno(giorni[n - 1])}`;
  return `${arco} · ${n} ${n === 1 ? "giorno" : "giorni"}`;
}

const GIORNI_RAPIDI = [1, 2, 3, 4, 5];

/* ── I bottoni dell'avviso: WhatsApp col messaggio già scritto, lo manda chi organizza ──── */
export function AvvisiLavoro({ lavoro }) {
  const persone = lavoro.squadra.filter((p) => p.avviso);
  if (!persone.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-inside" data-testid="montaggio-avvisi">
      {persone.map((p) => {
        const link = linkAvviso(p.telefono_e164, p.avviso);
        return link ? (
          <Button key={p.id} asChild variant="outline" size="sm" className="h-11 sm:h-8">
            <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`montaggio-avvisa-${p.id}`}>
              <MessageCircle />
              Avvisa {p.nome}
            </a>
          </Button>
        ) : (
          <span key={p.id} className="text-xs text-muted-foreground" data-testid={`montaggio-senza-telefono-${p.id}`}>
            {p.nome}: manca il telefono
          </span>
        );
      })}
    </div>
  );
}

/* ── Un lavoro già organizzato: i giorni, la squadra, l'avviso, l'annullo ────────────── */
export function LavoroMontaggio({ lavoro, onCambiato }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [busy, setBusy] = useState(false);
  const attivi = lavoro.giorni.filter((g) => g.stato !== "annullato");
  const spento = attivi.length === 0;

  const annulla = async () => {
    const ok = await chiedi({
      titolo: "Annullare il lavoro?",
      descrizione: "I giorni ancora da fare tornano liberi per la squadra. I giorni già fatti restano segnati.",
      verbo: "Annulla il lavoro",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/montaggio/${lavoro.lavoro_id}/annulla`);
      toast.success("Lavoro annullato: i giorni sono liberi");
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn("grid gap-inside rounded-lg border bg-card p-group", spento && "opacity-60")}
      data-testid={`lavoro-${lavoro.lavoro_id}`}
    >
      <p className={cn("text-sm font-medium text-foreground", spento && "line-through")}>
        {riepilogo((spento ? lavoro.giorni : attivi).map((g) => g.giorno))}
        {spento ? <span className="font-normal text-muted-foreground"> · annullato</span> : null}
      </p>
      <ul className="flex flex-wrap gap-inside" aria-label="I giorni del lavoro">
        {lavoro.giorni.map((g) => (
          <li
            key={g.id}
            data-testid={`lavoro-giorno-${g.giorno}`}
            data-stato={g.stato}
            className={cn(
              "rounded-md border px-inside py-micro text-xs tabular-nums",
              g.stato === "annullato" ? "border-border text-muted-foreground line-through" : "border-primary bg-brand-subtle text-brand-subtle-foreground",
            )}
          >
            {testaGiorno(g.giorno)}
            <span className="text-muted-foreground"> · {g.tipo === "consegna" ? "consegna" : "montaggio"}</span>
            {g.stato === "presentato" ? <span> · fatto</span> : g.stato === "no_show" ? <span> · non fatto</span> : null}
          </li>
        ))}
      </ul>
      {lavoro.n_squadra ? (
        <p className="flex items-center gap-inside text-sm text-foreground" data-testid="lavoro-squadra">
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {lavoro.squadra.length
            ? lavoro.squadra.map((p) => p.nome).join(" + ")
            : `${lavoro.n_squadra} ${lavoro.n_squadra === 1 ? "montatore" : "montatori"}`}
        </p>
      ) : null}
      {spento ? null : <AvvisiLavoro lavoro={lavoro} />}
      {lavoro.annullabile ? (
        <div>
          <Button type="button" variant="ghost" size="sm" className="h-11 text-muted-foreground sm:h-8" disabled={busy} onClick={annulla} data-testid="lavoro-annulla">
            Annulla il lavoro
          </Button>
        </div>
      ) : null}
      {dialogoConferma}
    </div>
  );
}

/* ── L'elenco dei montatori: nomi e telefoni, non account ───────────────────────────── */
function ElencoMontatori({ elenco, onCambiato }) {
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [busy, setBusy] = useState(false);

  const aggiungi = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/montaggio/montatori", { nome, telefono: telefono || null });
      setNome("");
      setTelefono("");
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  const cambia = async (m, campi) => {
    try {
      await api.patch(`/montaggio/montatori/${m.id}`, campi);
    } catch (err) {
      toast.error(erroreDi(err));
    }
    onCambiato();
  };

  return (
    <div className="grid gap-card">
      {elenco.length ? (
        <ul className="grid gap-group" data-testid="montatori-elenco">
          {elenco.map((m) => (
            <li key={`${m.id}-${m.telefono}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-inside">
              <span className="truncate text-sm font-medium text-foreground">{m.nome}</span>
              <Input
                aria-label={`Telefono di ${m.nome}`}
                inputMode="tel"
                defaultValue={m.telefono || ""}
                placeholder="Telefono"
                onBlur={(e) => (e.target.value.trim() !== (m.telefono || "") ? cambia(m, { telefono: e.target.value }) : null)}
                data-testid={`montatore-telefono-${m.id}`}
              />
              <Button type="button" variant="ghost" size="sm" className="h-11 text-muted-foreground sm:h-9" aria-label={`Togli ${m.nome} dall'elenco`} onClick={() => cambia(m, { attivo: false })} data-testid={`montatore-togli-${m.id}`}>
                Togli
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nessun montatore in elenco. Aggiungi chi monta: il telefono serve per avvisarlo.</p>
      )}
      <form onSubmit={aggiungi} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-inside">
        <div className="grid gap-inside">
          <Label htmlFor="mont-nome">Nome</Label>
          <Input id="mont-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} data-testid="montatore-nome" />
        </div>
        <div className="grid gap-inside">
          <Label htmlFor="mont-tel">Telefono</Label>
          <Input id="mont-tel" inputMode="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="338 123 4567" data-testid="montatore-tel" />
        </div>
        <Button type="submit" variant="outline" className="h-11 sm:h-9" disabled={busy || !nome.trim()} data-testid="montatore-aggiungi">
          Aggiungi
        </Button>
      </form>
    </div>
  );
}

/* ── «Organizza consegna e montaggio» ────────────────────────────────────────────────── */
export function OrganizzaMontaggioDialog({ open, onOpenChange, cliente, arrivo, onFatto }) {
  const [vista, setVista] = useState("form"); // form · elenco · fatto
  const [elenco, setElenco] = useState(null); // null = lo sto leggendo
  const [quanti, setQuanti] = useState(2);
  const [inizio, setInizio] = useState("");
  const [toccati, setToccati] = useState(new Set());
  const [squadra, setSquadra] = useState(new Set());
  const [note, setNote] = useState("");
  const [conflitti, setConflitti] = useState(null); // null = non ancora guardato
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lavoro, setLavoro] = useState(null);

  /* L'elenco si rilegge a ogni apertura: chi è stato aggiunto o tolto da un'altra apertura, o da un'altra persona, c'è o non c'è più. */
  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setVista("form");
    setElenco(null);
    api
      .get("/montaggio/montatori")
      .then((r) => {
        if (!vivo) return;
        setElenco(r.data.montatori);
        if (!r.data.montatori.length) setVista("elenco");
      })
      .catch((err) => vivo && (toast.error(erroreDi(err)), setElenco([])));
    setQuanti(2);
    setInizio(primoGiornoProposto(arrivo, oggi()));
    setToccati(new Set());
    setSquadra(new Set());
    setNote("");
    setConflitti(null);
    setErrore(null);
    setLavoro(null);
    return () => {
      vivo = false;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- si azzera all'apertura, non quando la scheda si ricarica

  const finestra = useMemo(() => (inizio ? giorniDelLavoro(inizio, quanti, toccati) : []), [inizio, quanti, toccati]);
  const giorni = useMemo(() => finestra.filter((g) => g.stato === "scelto").map((g) => g.giorno), [finestra]);
  const chiaveGiorni = giorni.join(",");
  const chiaveSquadra = [...squadra].sort().join(",");

  /* Chi è già impegnato quei giorni si dice PRIMA di salvare, non dopo il rifiuto. */
  useEffect(() => {
    setConflitti(null); // l'esito di prima non vale più per questa scelta
    setErrore(null);
    if (!open || vista !== "form" || !squadra.size || !giorni.length) return undefined;
    let vivo = true;
    const t = setTimeout(() => {
      api
        .post("/montaggio/verifica", { montatori: [...squadra], giorni })
        .then((r) => vivo && setConflitti(r.data))
        .catch(() => vivo && setConflitti({ conflitti: [], testo: null })); // se la domanda cade, il server ricontrolla al salvataggio
    }, 250);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [open, vista, chiaveGiorni, chiaveSquadra]); // eslint-disable-line react-hooks/exhaustive-deps

  const rileggiElenco = useCallback(async () => {
    try {
      const r = await api.get("/montaggio/montatori");
      setElenco(r.data.montatori);
      setSquadra((s) => new Set([...s].filter((id) => r.data.montatori.some((m) => m.id === id))));
    } catch (err) {
      toast.error(erroreDi(err));
    }
  }, []);

  const inConflitto = new Set((conflitti?.conflitti || []).map((c) => c.giorno));
  const alterna = (insieme, setInsieme, valore) => {
    const n = new Set(insieme);
    if (n.has(valore)) n.delete(valore);
    else n.add(valore);
    setInsieme(n);
  };

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrore(null);
    try {
      const r = await api.post("/montaggio", { cliente_id: cliente.id, giorni, montatori: [...squadra], note: note.trim() || null });
      setLavoro(r.data);
      setVista("fatto");
      onFatto(r.data);
    } catch (err) {
      setErrore(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  const sett = arrivo ? settimanaDi(arrivo) : null;
  // «Organizza» si accende solo quando il controllo di QUESTA scelta è tornato e dice che è libera.
  const bloccato = busy || !squadra.size || !giorni.length || !conflitti || conflitti.conflitti.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="montaggio-modal">
        {vista === "fatto" && lavoro ? (
          <div className="grid gap-card">
            <DialogHeader>
              <DialogTitle>Organizzato</DialogTitle>
              <DialogDescription data-testid="montaggio-esito">
                {riepilogo(lavoro.giorni.map((g) => g.giorno))} · {lavoro.squadra.map((p) => p.nome).join(" + ")}. I giorni sono occupati per la squadra.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-inside">
              <p className="text-sm text-foreground">Avvisa chi monta: si apre WhatsApp col messaggio già scritto.</p>
              <AvvisiLavoro lavoro={lavoro} />
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="montaggio-chiudi">
                Chiudi
              </Button>
            </DialogFooter>
          </div>
        ) : vista === "elenco" ? (
          <div className="grid gap-card">
            <DialogHeader>
              <DialogTitle>Montatori</DialogTitle>
              <DialogDescription>Le persone che montano. Sono nomi con un telefono, non account: l'avviso lo mandi tu da WhatsApp.</DialogDescription>
            </DialogHeader>
            <ElencoMontatori elenco={elenco || []} onCambiato={rileggiElenco} />
            <DialogFooter>
              <Button type="button" onClick={() => setVista("form")} disabled={!elenco?.length} data-testid="montatori-torna">
                {elenco?.length ? "Torna ai giorni" : "Aggiungi almeno un montatore"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={salva} className="grid gap-card">
            <DialogHeader>
              <DialogTitle>Organizza consegna e montaggio</DialogTitle>
              <DialogDescription>
                {cliente.nome_visualizzato}. Scegli quanti giorni servono e chi va: quei giorni restano occupati per la squadra.
              </DialogDescription>
            </DialogHeader>

            <p className={cn("text-sm", sett ? "text-foreground" : "text-muted-foreground")} data-testid="montaggio-arrivo">
              {sett
                ? `La merce arriva nella settimana ${sett.numero}: ${giorniDellaSettimana(arrivo)}.`
                : "La settimana di arrivo della merce non è ancora segnata."}
            </p>

            <div className="grid gap-group">
              <fieldset className="grid gap-inside">
                <legend className="mb-inside text-sm font-medium text-foreground">Quanti giorni</legend>
                <div className="grid grid-cols-5 gap-inside">
                  {GIORNI_RAPIDI.map((n) => (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={quanti === n}
                      onClick={() => setQuanti(n)}
                      data-testid={`montaggio-quanti-${n}`}
                      className={cn(
                        "min-h-11 rounded-md border px-inside text-sm transition-colors sm:min-h-9",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        quanti === n ? "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground" : "border-border bg-card text-foreground hover:bg-accent",
                      )}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="grid gap-inside">
                <Label htmlFor="mont-inizio">Primo giorno</Label>
                <CampoData
                  id="mont-inizio"
                  value={inizio}
                  min={oggi()}
                  onChange={(e) => {
                    setInizio(e.target.value);
                    setToccati(new Set());
                  }}
                  testId="montaggio-inizio"
                  required
                />
              </div>
            </div>

            <fieldset className="grid gap-inside">
              <legend className="mb-inside text-sm font-medium text-foreground">I giorni</legend>
              <ul className="flex flex-wrap gap-inside" data-testid="montaggio-giorni">
                {finestra.map((g) => {
                  const scelto = g.stato === "scelto";
                  const primo = scelto && g.giorno === giorni[0];
                  const conflitto = scelto && inConflitto.has(g.giorno);
                  return (
                    <li key={g.giorno}>
                      <button
                        type="button"
                        disabled={g.stato === "domenica"}
                        aria-pressed={scelto}
                        onClick={() => alterna(toccati, setToccati, g.giorno)}
                        data-testid={`montaggio-giorno-${g.giorno}`}
                        data-stato={g.stato}
                        data-conflitto={conflitto ? "si" : undefined}
                        aria-label={`${testaGiorno(g.giorno)}${primo ? ", consegna" : ""}${conflitto ? ", squadra già impegnata" : ""}${g.stato === "domenica" ? ", domenica" : ""}`}
                        className={cn(
                          "grid min-h-11 min-w-16 content-center rounded-md border px-inside text-sm tabular-nums transition-colors sm:min-h-10",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed",
                          scelto && !conflitto && "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground",
                          conflitto && "border-danger bg-danger-subtle font-medium text-danger-foreground",
                          g.stato === "saltato" && "border-dashed border-border bg-card text-muted-foreground line-through hover:bg-accent",
                          g.stato === "domenica" && "border-border bg-muted text-muted-foreground opacity-60",
                        )}
                      >
                        {testaGiorno(g.giorno)}
                        {primo ? <span className="text-xs font-normal">consegna</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground">
                Il primo giorno è la consegna, gli altri il montaggio, dalle 7 alle 18. Tocca un giorno per saltarlo: il lavoro si allunga
                di uno. Il sabato è saltato: toccalo se quella settimana si lavora.
              </p>
            </fieldset>

            <fieldset className="grid gap-inside">
              <legend className="mb-inside text-sm font-medium text-foreground">Squadra</legend>
              <div className="flex flex-wrap items-center gap-inside">
                {elenco === null ? <p className="text-sm text-muted-foreground">Leggo l'elenco dei montatori…</p> : null}
                {(elenco || []).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    aria-pressed={squadra.has(m.id)}
                    onClick={() => alterna(squadra, setSquadra, m.id)}
                    data-testid={`montaggio-squadra-${m.id}`}
                    className={cn(
                      "min-h-11 rounded-md border px-group text-sm transition-colors sm:min-h-9",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      squadra.has(m.id) ? "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground" : "border-border bg-card text-foreground hover:bg-accent",
                    )}
                  >
                    {m.nome}
                  </button>
                ))}
                <Button type="button" variant="ghost" size="sm" className="h-11 text-muted-foreground sm:h-9" onClick={() => setVista("elenco")} data-testid="montaggio-gestisci">
                  <Settings2 />
                  Gestisci l'elenco
                </Button>
              </div>
            </fieldset>

            <div className="grid gap-inside">
              <Label htmlFor="mont-note">Note (facoltativo)</Label>
              <Input id="mont-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Es. cucina con living e camere" data-testid="montaggio-note" />
            </div>

            {conflitti?.conflitti?.length ? (
              <p role="alert" className="flex gap-inside rounded-md border border-danger/30 bg-danger-subtle px-group py-inside text-sm text-danger-foreground" data-testid="montaggio-conflitto">
                <AlertCircle className="mt-micro h-4 w-4 shrink-0" aria-hidden />
                <span>{conflitti.testo}. Cambia i giorni o la squadra.</span>
              </p>
            ) : null}
            {errore ? (
              <p role="alert" className="rounded-md border border-danger/30 bg-danger-subtle px-group py-inside text-sm text-danger-foreground" data-testid="montaggio-errore">
                {errore}
              </p>
            ) : null}

            <DialogFooter className="flex-col-reverse gap-inside sm:flex-row">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={bloccato} data-testid="montaggio-salva">
                {busy ? "Salvo…" : "Organizza"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
